import React, { useEffect, useState, useCallback } from 'react';
import {
  buildBackendAisleName,
  resolveLinearName,
} from 'core/slices/utils/aisleNames';
import { useTranslation } from 'react-i18next';
import { resolveLegacyTaskType } from 'core/slices/utils/constants';
import { useTheme } from '@mui/material/styles';
import { scrollbarStyles } from '../../styleSheet';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Autocomplete,
  CircularProgress,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import BaseModal from '../templates/BaseModal';
import ImageCarousel from '../templates/ImageCarousel';
import DetailBlock from '../templates/DetailBlock';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  mainContainer: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },

  // Header
  header: {
    bgcolor: 'background.paper', px: 2.5, pt: 2.5, pb: 2, flexShrink: 0,
    borderBottom: '1px solid', borderColor: 'divider',
  },
  headerContent: { display: 'flex', alignItems: 'flex-start', gap: 1.5 },
  headerIcon: {
    width: 44, height: 44, borderRadius: 2.5, bgcolor: theme => theme.bruno.colors.inactive,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.25,
  },
  headerText: { flexGrow: 1, minWidth: 0 },
  headerTitle: { lineHeight: 1.35 },
  headerMeta: { display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0, mt: 0.3 },
  closeButton: { ml: 0.5, flexShrink: 0 },

  // Tabs
  tabs: {
    flexShrink: 0,
    borderBottom: '1px solid', borderColor: 'divider',
    bgcolor: 'background.paper',
    minHeight: 40,
    '& .MuiTab-root': { minHeight: 40, fontSize: '0.75rem', fontWeight: 600, textTransform: 'none', flex: 1 },
  },

  // Body
  body: {
    flexGrow: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 2,
    scrollbarGutter: 'stable',
    ...scrollbarStyles,
  },
  bodyContent: { display: 'flex', flexDirection: 'column', gap: 1.5 },

  // Barcode image
  barcodeBlock: {
    bgcolor: theme => theme.bruno.colors.inactive, borderRadius: 3,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: 240, overflow: 'hidden', flexShrink: 0,
  },
  barcodeImg: { maxWidth: '100%', maxHeight: 120, objectFit: 'contain' },
  barcodeEmptyState: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
  },

  // Loading / error
  loadingContainer: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', py: 8, gap: 1.5,
  },
  errorIconBox: {
    width: 64, height: 64, borderRadius: 3, bgcolor: theme => theme.bruno.colors.inactive,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  errorText: { fontSize: 13, color: 'text.secondary', textAlign: 'center' },

  // Footer
  footer: {
    bgcolor: 'background.paper',
    borderTop: '1px solid', borderColor: 'divider',
    px: 6, flexShrink: 0, width: '100%', minHeight: 100,
    display: 'flex', flexDirection: 'column', justifyContent: 'center',
    gap: 1.5, py: 1.5,
    position: 'relative',
  },
  footerCloseButton: {
    position: 'absolute', top: 8, right: 8,
    color: 'text.secondary',
  },
  footerLabel: {
    fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.1em',
    textTransform: 'uppercase', color: 'text.disabled', ml: 0.25, mb: 0.75, display: 'block',
  },
  reportRow: { display: 'flex', alignItems: 'flex-end', gap: 1.5 },
  reportSelectWrapper: { flex: 1, display: 'flex', flexDirection: 'column' },
  reportAutocomplete: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      bgcolor: 'background.paper',
      height: 40,
      '& fieldset': { borderColor: 'divider' },
      '&:hover fieldset': { borderColor: 'text.disabled' },
      '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: '1px' },
      '& .MuiOutlinedInput-input': { py: 0 },
    },
    '& .MuiInputBase-input': { fontWeight: 400, fontSize: '0.8rem' },
  },
  stockSection: { display: 'flex', flexDirection: 'column' },
  stockHelperText: {
    fontSize: '0.5625rem', color: 'text.secondary', ml: 0.25, mt: 0.5,
  },
  stockField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      bgcolor: 'background.paper',
      height: 40,
      '& fieldset': { borderColor: 'divider' },
      '&:hover fieldset': { borderColor: 'text.disabled' },
      '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: '1px' },
      '& .MuiOutlinedInput-input': { py: 0, fontWeight: 600, fontSize: '0.8rem' },
    },
  },
  reportButton: {
    width: 40, height: 40, flexShrink: 0,
    bgcolor: '#e91e63', color: '#fff',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    '&:hover': { bgcolor: '#c2185b', transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(233,30,99,0.45)' },
    '&:disabled': { bgcolor: '#e91e63', color: '#fff', opacity: 0.4 },
  },
  reportButtonSpinner: { color: '#fff' },
  reportButtonIcon: { fontSize: 20 },

  // Header icon
  headerIconImg: { fontSize: 22, color: 'text.secondary' },

  // Error state icon
  errorIcon: { fontSize: 30, color: 'text.disabled' },

  // Barcode empty state icon
  barcodeEmptyIcon: { fontSize: 36, color: 'text.disabled' },

};

// ── Sub-components ─────────────────────────────────────────────────────────────

function SkeletonBlock() {
  return (
    <>
      <Box sx={styles.barcodeBlock}>
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="rounded" width={64} height={64} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rounded" width={100} height={12} sx={{ borderRadius: 1 }} />
        </Box>
      </Box>
      <Box sx={{ bgcolor: 'background.paper', borderRadius: 3, px: 2, pt: 1, pb: 0.5, overflow: 'hidden' }}>
        {[80, 120, 60, 100].map((w, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', py: 1.25, gap: 1.5 }}>
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="text" width={w} height={16} sx={{ flexGrow: 1 }} />
            <Skeleton variant="text" width={60} height={16} />
          </Box>
        ))}
      </Box>
    </>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function ProductDetailModal({
  open,
  aislesLayout,
  product,
  productInfo,
  productInfoLoading,
  productImages,
  productBarcodeUrl,
  reportOptions,
  fetchProductInfo,
  reportProduct,
  onClose,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedReport, setSelectedReport] = useState(null);
  const [realStock, setRealStock] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [barcodeError, setBarcodeError] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const isOpenRef = React.useRef(false);

  useEffect(() => {
    isOpenRef.current = open;
    if (open && product) {
      setSelectedReport(null);
      setRealStock('');
      setActiveTab(0);
      setBarcodeError(false);
      fetchProductInfo({ item: product.item, ean: product.ean });
    }
    // fetchProductInfo is a stable Redux dispatch wrapper
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product?.item, product?.ean]);

  const handleClose = useCallback(() => {
    setSelectedReport(null);
    setRealStock('');
    setActiveTab(0);
    setBarcodeError(false);
    onClose();
  }, [onClose]);

  const handleReport = useCallback(async () => {
    if (!selectedReport) return;
    setReportLoading(true);
    try {
      const result = await reportProduct({
        item: product.item,
        ean: product.ean,
        taskType: selectedReport.task_type,
        realStock: selectedReport.ask_real_stock ? realStock : undefined,
      });
      if (result?.success && isOpenRef.current) handleClose();
    } finally {
      setReportLoading(false);
    }
  }, [selectedReport, realStock, reportProduct, product, handleClose]);

  const isValidReport = selectedReport !== null &&
    (!selectedReport.ask_real_stock || (realStock !== '' && !isNaN(Number(realStock)) && Number(realStock) >= 0));

  const stockValue = productInfo?.stock ?? '-';

  if (!product) return null;

  const content = (
    <Box sx={styles.mainContainer}>
      {/* ── HEADER ── */}
      <Box sx={styles.header}>
        <Box sx={styles.headerContent}>
          <Box sx={styles.headerIcon}>
            <InventoryIcon sx={styles.headerIconImg} />
          </Box>
          <Box sx={styles.headerText}>
            <Typography variant="subtitle1" fontWeight={700} noWrap sx={styles.headerTitle}>
              {product.description || '-'}
            </Typography>
            <Box sx={styles.headerMeta}>
              <Typography variant="caption" color="text.secondary">
                {t('bruno.products.item', 'Item')} <strong>{product.item || '-'}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('bruno.products.barcode', 'Barcode')} <strong>{product.ean || '-'}</strong>
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleClose} sx={styles.closeButton} disabled={reportLoading}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* ── TABS ── */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth" sx={styles.tabs}>
        <Tab label={t('bruno.tasks_details.details', 'Details')} />
        <Tab label={t('bruno.products.captures', 'Captures')} />
      </Tabs>

      {/* ── TAB: INFO ── */}
      {activeTab === 0 && (
        <Box sx={styles.body}>
          <Box sx={styles.bodyContent}>
            {productInfoLoading ? (
              <SkeletonBlock />
            ) : !productInfo ? (
              <Box sx={styles.loadingContainer}>
                <Box sx={styles.errorIconBox}>
                  <InventoryIcon sx={styles.errorIcon} />
                </Box>
                <Typography sx={styles.errorText}>
                  {t('bruno.products.info_not_found', 'Product information not found')}
                </Typography>
              </Box>
            ) : (
              <>
                {/* ── Barcode image ── */}
                <Box sx={styles.barcodeBlock}>
                  {productBarcodeUrl && !barcodeError ? (
                    <img src={productBarcodeUrl} alt="barcode" style={styles.barcodeImg} onError={() => setBarcodeError(true)} />
                  ) : (
                    <Box sx={styles.barcodeEmptyState}>
                      <Box component="span" className="material-icons" sx={styles.barcodeEmptyIcon}>image_not_supported</Box>
                      <Typography variant="caption" color="text.disabled">{t('bruno.products.no_barcode', 'No barcode')}</Typography>
                    </Box>
                  )}
                </Box>

                {/* ── Details block ── */}
                <DetailBlock
                  title={t('bruno.tasks_details.details', 'Details')}
                  rows={[
                    { icon: 'inventory_2', label: t('bruno.products.stock', 'Stock'), value: stockValue },
                    { icon: 'sell', label: t('bruno.products.pos_price', 'POS price'), value: productInfo.price ?? '-' },
                  ]}
                />

                {productInfo.placement?.length > 0 && (
                  <DetailBlock
                    title={t('bruno.products.placements', 'Placements')}
                    rows={productInfo.placement.flatMap((p, i) => {
                      const backendName = buildBackendAisleName(
                        p.aisle,
                        p.orientation,
                        p.aisle_part,
                      );
                      const parts =
                        resolveLinearName(aislesLayout, backendName) ??
                        backendName;
                      return [
                        ...(i > 0 ? [{ divider: 'dashed' }] : []),
                        { icon: 'pin_drop', label: t('bruno.tasks_details.aisle', 'Aisle'), value: parts || '-' },
                        { icon: 'schedule', label: t('bruno.products.last_seen', 'Last seen'), value: p.last_time_seen || '-' },
                      ];
                    })}
                  />
                )}

                {productInfo.adjustment?.length > 0 && (
                  <DetailBlock
                    title={t('bruno.products.adjustments', 'Adjustments')}
                    rows={productInfo.adjustment.flatMap((a, i) => [
                      ...(i > 0 ? [{ divider: 'dashed' }] : []),
                      { icon: 'event', label: t('bruno.products.requested', 'Requested'), value: a.request_timestamp || '-' },
                      { icon: 'info', label: t('bruno.products.status', 'Status'), value: a.status || '-' },
                      { icon: 'update', label: t('bruno.products.updated', 'Updated'), value: a.status_change_timestamp || '-' },
                    ])}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
      )}

      {/* ── TAB: CAPTURES ── */}
      {activeTab === 1 && (
        <Box sx={styles.body}>
          <Box sx={styles.bodyContent}>
            <ImageCarousel
              images={productImages}
              loading={productInfoLoading}
              isMobile={isMobile}
              renderMetadata={(current) => {
                const m = current?.metadata;
                if (!m || (!m.date && !m.aisle && !m.landmark && m.location_x == null)) return null;
                return (
                  <DetailBlock
                    rows={[
                      m.date && { icon: 'schedule', label: t('bruno.products.last_seen', 'Last seen'), value: m.date },
                      m.aisle && { icon: 'pin_drop', label: t('bruno.tasks_details.aisle', 'Aisle'), value: m.aisle },
                      m.landmark && { icon: 'place', label: t('bruno.tasks_details.landmark', 'Landmark'), value: m.landmark },
                      m.location_x != null && { icon: 'straighten', label: t('bruno.products.linear', 'Linear'), value: `${parseFloat(m.location_x).toFixed(2)} m` },
                    ].filter(Boolean)}
                  />
                );
              }}
            />
          </Box>
        </Box>
      )}

      {/* ── FOOTER ── */}
      <Box sx={styles.footer}>
        {selectedReport?.ask_real_stock && (
          <IconButton size="small" onClick={() => { setSelectedReport(null); setRealStock(''); }} sx={styles.footerCloseButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
        {/* ── Report selector + check button ── */}
        <Box sx={styles.reportRow}>
          <Box sx={styles.reportSelectWrapper}>
            <Typography sx={styles.footerLabel}>
              {t('bruno.products.report', 'Report')}
            </Typography>
            <Autocomplete
              fullWidth
              options={reportOptions || []}
              getOptionLabel={(option) => t(`bruno.tasks_names.${resolveLegacyTaskType(option.task_type)}`, option.task_type)}
              value={selectedReport}
              onChange={(_, value) => { setSelectedReport(value); setRealStock(''); }}
              disabled={productInfoLoading || !productInfo || reportLoading}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('bruno.products.report_placeholder', 'Select a report type')}
                  sx={styles.reportAutocomplete}
                />
              )}
            />
          </Box>
          <IconButton
            disabled={!isValidReport || reportLoading}
            onClick={handleReport}
            sx={styles.reportButton}
          >
            {reportLoading ? (
              <CircularProgress size={18} sx={styles.reportButtonSpinner} />
            ) : (
              <span className="material-icons" style={styles.reportButtonIcon}>check</span>
            )}
          </IconButton>
        </Box>

        {/* ── Real stock — conditional ── */}
        {selectedReport?.ask_real_stock && (
          <Box sx={styles.stockSection}>
            <Typography sx={styles.footerLabel}>
              {t('bruno.products.real_stock_label', 'Real stock (optional)')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="0"
              value={realStock}
              onChange={(e) => setRealStock(e.target.value)}
              inputProps={{ min: 0 }}
              sx={styles.stockField}
            />
            <Typography sx={styles.stockHelperText}>
              {t('bruno.products.real_stock_helper', 'Optional — enter the actual units found on shelf')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <BaseModal open={open} onClose={handleClose}>
      {content}
    </BaseModal>
  );
}
